import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Medal, Award, Crown, Info, User } from "lucide-react";
import { trophiesApi, sportsApi } from "@/lib/api";
import { UserAvatar, EmptyState } from "@/components/shared";
import { StaggerGroup, StaggerItem, AnimatedCounter, SkeletonList } from "@/components/motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

const RANK_STYLE = {
  1: { ring: "ring-2 ring-trophy-gold", icon: Crown, color: "text-trophy-gold", bg: "bg-trophy-gold/10" },
  2: { ring: "ring-2 ring-trophy-silver", icon: Medal, color: "text-trophy-silver", bg: "bg-trophy-silver/10" },
  3: { ring: "ring-2 ring-trophy-bronze", icon: Award, color: "text-trophy-bronze", bg: "bg-trophy-bronze/10" },
};
const PERIOD_LBL = { week: "hebdomadaire", month: "mensuelle", year: "annuelle" };

function Ranking({ title, rows }) {
  if (!rows || !rows.length) return (
    <div><h3 className="mb-2 font-heading font-semibold">{title}</h3><EmptyState title="Aucun classement" description="Les récompenses apparaîtront ici." icon={Trophy} /></div>
  );
  return (
    <div>
      <h3 className="mb-2 font-heading font-semibold">{title}</h3>
      <StaggerGroup className="space-y-2">
        {rows.map((r) => {
          const st = RANK_STYLE[r.rank];
          return (
            <StaggerItem key={r.recipient.id}>
              <Card className={cn(st?.ring)} data-testid="honneur-ligne"><CardContent className="flex items-center gap-3 p-3">
                <div className={cn("flex h-9 w-9 items-center justify-center rounded-full font-heading font-bold", st ? st.bg : "bg-muted", st ? st.color : "text-muted-foreground")}>
                  {st ? <st.icon className="h-4 w-4" /> : r.rank}
                </div>
                <UserAvatar user={r.recipient} size="h-10 w-10" className={cn(st?.ring)} />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold">{r.recipient.first_name} {r.recipient.last_name}</div>
                  <div className="text-[11px] text-muted-foreground">Dernière distinction : {r.last_period ? PERIOD_LBL[r.last_period] : "—"}</div>
                </div>
                <div className="hidden gap-4 sm:flex">
                  <div className="text-center"><div className="text-sm font-bold tabular-nums">{r.weekly}</div><div className="text-[10px] text-muted-foreground">Hebdo</div></div>
                  <div className="text-center"><div className="text-sm font-bold tabular-nums">{r.monthly}</div><div className="text-[10px] text-muted-foreground">Mois</div></div>
                </div>
                <div className="flex items-center gap-1.5 rounded-lg bg-trophy-gold/10 px-2.5 py-1"><Trophy className="h-4 w-4 text-trophy-gold" /><AnimatedCounter value={r.total} className="text-base font-heading font-bold tabular-nums" /></div>
                <Link to={`/profil/${r.recipient.id}`} aria-label="Voir le profil"><Button size="sm" variant="ghost" data-testid="honneur-profil"><User className="h-4 w-4" /></Button></Link>
              </CardContent></Card>
            </StaggerItem>
          );
        })}
      </StaggerGroup>
    </div>
  );
}

export function MurHonneur() {
  const [sports, setSports] = useState([]);
  const [year, setYear] = useState(null);
  const [sportId, setSportId] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { sportsApi.list().then((r) => setSports(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (year) params.year = year;
        if (sportId !== "all") params.sport_id = sportId;
        const r = await trophiesApi.hallOfFame(params);
        setData(r.data);
        if (!year && r.data.year) setYear(r.data.year);
      } finally { setLoading(false); }
    })();
  }, [year, sportId]);

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-heading text-lg font-bold"><Crown className="h-5 w-5 text-trophy-gold" />Mur d'honneur{data?.year ? ` · ${data.year}` : ""}</h2>
        <div className="flex gap-2">
          <Select value={year ? String(year) : ""} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-28" data-testid="honneur-annee-select"><SelectValue placeholder="Année" /></SelectTrigger>
            <SelectContent>{(data?.available_years || []).map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={sportId} onValueChange={setSportId}>
            <SelectTrigger className="w-36" data-testid="honneur-sport-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tous les sports</SelectItem>{sports.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {data?.tiebreak_rule && (
        <div className="flex items-start gap-2 rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground" data-testid="honneur-regle-egalite"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />{data.tiebreak_rule}</div>
      )}
      {loading ? <SkeletonList count={4} /> : (
        <div className="grid gap-6 lg:grid-cols-2">
          <Ranking title="Classement des athlètes" rows={data?.athletes} />
          <Ranking title="Classement des entraîneurs" rows={data?.coaches} />
        </div>
      )}
    </section>
  );
}
